// The one safe way to run a pattern that arrived from upstream (CORE) against
// what a payer typed. Shared by the configured-fields renderer and the
// payment-method number check, so the denial-of-service guard exists once.

// T-16-20-02. An upstream pattern run against payer input is a catastrophic-
// backtracking target. Length is the WRONG axis and a length cap alone is no
// mitigation at all: `^(a+)+$` is seven characters and took 43s on 30 of
// input, measured in this engine. What actually costs is a quantifier applied
// to a group that already contains one, so that is what gets refused.
export const MAX_PATTERN_LENGTH = 200;
export const MAX_TESTED_LENGTH = 256;

// Star height > 1. Measured: every pattern this refuses ran for tens of
// seconds; every pattern it allows, including the repo's own email regex,
// returned in 0ms. A refusal costs the payer instant feedback on one field
// and never blocks the payment, so erring toward refusing is the safe side.
export const hasNestedQuantifier = (pattern: string): boolean => {
  const quantifiedAtDepth: boolean[] = [];
  let depth = 0;

  for (let i = 0; i < pattern.length; i++) {
    const char = pattern[i];

    if (char === "\\") {
      i++;
      continue;
    }

    if (char === "(") {
      depth++;
      quantifiedAtDepth[depth] = false;
      continue;
    }

    if (char === ")") {
      const innerHadQuantifier = quantifiedAtDepth[depth] === true;
      depth--;
      const next = pattern[i + 1];
      const groupIsQuantified = next === "*" || next === "+" || next === "{";

      if (innerHadQuantifier && groupIsQuantified) return true;
      if (groupIsQuantified && depth >= 1) quantifiedAtDepth[depth] = true;
      continue;
    }

    if ((char === "*" || char === "+" || char === "{") && depth >= 1) {
      quantifiedAtDepth[depth] = true;
    }
  }

  return false;
};

const DELIMITED_PATTERN = /^\/(.*)\/([a-z]*)$/;

// CORE sometimes sends a pattern wrapped in slash delimiters with flags.
const unwrapDelimiters = (regex: string): { pattern: string; flags: string } => {
  const delimited = regex.match(DELIMITED_PATTERN);
  return delimited
    ? { pattern: delimited[1], flags: delimited[2] }
    : { pattern: regex, flags: "" };
};

// A pattern that will not compile, or is long enough to be a denial-of-service
// vector, yields no validator rather than an exception or a blocked payer.
export const compilePattern = (regex: string | undefined): RegExp | null => {
  if (!regex || regex.length > MAX_PATTERN_LENGTH) return null;

  const { pattern, flags } = unwrapDelimiters(regex);
  if (hasNestedQuantifier(pattern)) return null;

  try {
    return new RegExp(pattern, flags);
  } catch {
    return null;
  }
};

// True when any candidate matches. lastIndex is reset each time because a
// `g` or `y` flag makes RegExp.test carry state from one call to the next.
export const testsAny = (pattern: RegExp, candidates: string[]): boolean =>
  candidates.some(candidate => {
    pattern.lastIndex = 0;
    return pattern.test(candidate.slice(0, MAX_TESTED_LENGTH));
  });
